import type { TFunction } from "i18next";
import { z } from "zod";
import {
  CREATE_DEFICIENCY_FIELDS as F,
  MAX_DEFICIENCY_ATTACHMENTS,
  MAX_DEFICIENCY_DETAILS_LENGTH,
  MAX_DEFICIENCY_IMO_DIGITS,
  MAX_DEFICIENCY_REMARKS_LENGTH,
  MAX_TRIP_NO_PLDA_LENGTH,
} from "./constants";

const requiredString = (label: string, t: TFunction, max: number) =>
  z
    .string()
    .trim()
    .min(1, t("form-validation.mandatory", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const requiredDigitsString = (label: string, t: TFunction, maxDigits: number) =>
  z
    .string()
    .trim()
    .min(1, t("form-validation.mandatory", { label }))
    .regex(/^\d+$/, t("form-validation.invalid-number", { label }))
    .max(maxDigits, t("form-validation.max-length-digits", { label, maxDigits }));

const optionalString = (max: number) => z.string().trim().max(max).optional();

const optionalStringWithLabel = (label: string, t: TFunction, max: number) =>
  z.string().trim().max(max, t("form-validation.max-length", { label, max })).optional();

export const getCreateDeficiencyFormSchema = (t: TFunction) =>
  z.object({
    [F.IMO_NO]: requiredDigitsString(
      t("deficiencies.headerLabels.imoNo"),
      t,
      MAX_DEFICIENCY_IMO_DIGITS,
    ),
    [F.REGISTERED_VESSEL_ID]: optionalString(100),
    [F.VESSEL_NAME]: optionalString(150),
    [F.SHIPPING_AGENCY]: optionalString(150),
    [F.TRIP_NO_PLDA]: requiredString(
      t("deficiencies.headerLabels.tripNoPlda"),
      t,
      MAX_TRIP_NO_PLDA_LENGTH,
    ),
    [F.BERTH_VISITED]: requiredString(t("deficiencies.headerLabels.berthVisited"), t, 50),
    [F.LOA]: optionalString(20),
    [F.DEFICIENCY_DETAILS]: requiredString(
      t("deficiencies.headerLabels.deficiencyDetails"),
      t,
      MAX_DEFICIENCY_DETAILS_LENGTH,
    ),
    [F.REMARKS]: optionalStringWithLabel(
      t("deficiencies.headerLabels.remarks"),
      t,
      MAX_DEFICIENCY_REMARKS_LENGTH,
    ),
    [F.ATTACHMENTS]: z.array(z.any()).max(MAX_DEFICIENCY_ATTACHMENTS).optional(),
  });

export type CreateDeficiencyFormData = z.infer<ReturnType<typeof getCreateDeficiencyFormSchema>>;

export const createDeficiencyDefaultValues: CreateDeficiencyFormData = {
  [F.IMO_NO]: "",
  [F.REGISTERED_VESSEL_ID]: "",
  [F.VESSEL_NAME]: "",
  [F.SHIPPING_AGENCY]: "",
  [F.TRIP_NO_PLDA]: "",
  [F.BERTH_VISITED]: "",
  [F.LOA]: "",
  [F.DEFICIENCY_DETAILS]: "",
  [F.REMARKS]: "",
  [F.ATTACHMENTS]: [],
};
